import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import CalendarPage from './pages/CalendarPage';
import SchedulesPage from './pages/SchedulesPage';
import MyRequestsPage from './pages/MyRequestsPage';
import ApprovalsPage from './pages/ApprovalsPage';
import OrganizationPage from './pages/OrganizationPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import SettingsPage from './pages/SettingsPage';
import AnomaliesPage from './pages/AnomaliesPage';
import BalancesPage from './pages/BalancesPage';
import PendingActivationPage from './pages/PendingActivationPage';
import ShiftSwapsPage from './pages/ShiftSwapsPage';
import EmployeeHistoryPage from './pages/EmployeeHistoryPage';
import './App.css';

const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-12 h-12 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Outlet key={location.pathname} /></Layout>;
};

const RoleGuard = ({ allowedRoles, children }) => {
  const { user } = useAuth();
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-12 h-12 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/pending-activation" element={<PendingActivationPage />} />

      {/* All protected routes share a single Layout instance */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/schedules" element={<RoleGuard allowedRoles={['super_admin', 'manager', 'team_leader']}><SchedulesPage /></RoleGuard>} />
        <Route path="/my-requests" element={<MyRequestsPage />} />
        <Route path="/approvals" element={<RoleGuard allowedRoles={['super_admin', 'manager', 'team_leader']}><ApprovalsPage /></RoleGuard>} />
        <Route path="/anomalies" element={<RoleGuard allowedRoles={['super_admin', 'manager', 'team_leader']}><AnomaliesPage /></RoleGuard>} />
        <Route path="/shift-swaps" element={<ShiftSwapsPage />} />
        <Route path="/employee-history" element={<EmployeeHistoryPage />} />
        <Route path="/organization" element={<RoleGuard allowedRoles={['super_admin', 'manager']}><OrganizationPage /></RoleGuard>} />
        <Route path="/users" element={<RoleGuard allowedRoles={['super_admin']}><UsersPage /></RoleGuard>} />
        <Route path="/balances" element={<RoleGuard allowedRoles={['super_admin', 'manager']}><BalancesPage /></RoleGuard>} />
        <Route path="/reports" element={<RoleGuard allowedRoles={['super_admin', 'manager', 'team_leader']}><ReportsPage /></RoleGuard>} />
        <Route path="/activity-logs" element={<RoleGuard allowedRoles={['super_admin', 'manager']}><ActivityLogsPage /></RoleGuard>} />
        <Route path="/settings" element={<RoleGuard allowedRoles={['super_admin']}><SettingsPage /></RoleGuard>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    // React Router 7 usa startTransition per gli aggiornamenti di navigazione: con React 18/19
    // la vista può restare indietro rispetto all'URL finché non si forza un refresh.
    <BrowserRouter unstable_useTransitions={false}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
